import React, { useContext, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import AppContext from '../../Context/AppContext';
import {
    resolveNonSubscriberPathClassic,
    resolveUserStepPathClassic,
} from '../../util/portalV2';
import oolaLogo from '../../Assets/oola_w.svg';
import './LoginPage.css';

/**
 * V1 login, kept at /login_old and /home_old.
 */
const LoginPage = () => {
    const { login, checkUserSession } = useContext(AppContext);
    const history = useHistory();
    const location = useLocation();
    const formRef = useRef(null);
    const userIdRef = useRef(null);

    const [typeLogin, setTypeLogin] = useState(1);
    const [textUserId, setTextUserId] = useState('');
    const [errorLogin, setErrorLogin] = useState({
        severity: 0,
        visible: 0,
        message: '',
    });

    useEffect(() => {
        const session = checkUserSession();
        if (session?.token) {
            history.replace(resolveUserStepPathClassic(session.userStepId));
        }
    }, [checkUserSession, history]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('error') === 'auth_failed') {
            setErrorLogin({
                severity: 2,
                visible: 1,
                message:
                    'We could not sign you in. Please try again with your credentials.',
            });
        }
    }, [location.search]);

    useEffect(() => {
        const t = window.setTimeout(() => {
            if (userIdRef.current) userIdRef.current.focus();
        }, 200);
        return () => window.clearTimeout(t);
    }, [typeLogin]);

    const handleLogin = async (event) => {
        event.preventDefault();

        const formData = new FormData(formRef.current);
        const phone = formData.get('phone');
        const password = formData.get('password');

        const result = await login(phone, password, typeLogin);
        if (typeof result === 'number') {
            if (result === -1) {
                history.push(resolveNonSubscriberPathClassic());
            } else {
                history.push(resolveUserStepPathClassic(result));
            }
        } else if (result?.data) {
            setErrorLogin({
                severity: result.data.severity ?? 1,
                visible: 1,
                message: result.data.message ?? '',
            });
            if (result.data.severity === 3) {
                setTextUserId('');
                setTypeLogin(2);
            }
        }
    };

    return (
        <div className="login-page">
            <div className="login-page__panel">
                <img
                    src={oolaLogo}
                    alt="Oola"
                    className="login-page__logo"
                />
                <p className="login-page__tagline">The Mind and Body Company</p>

                <h1 className="login-page__title">LOG IN</h1>

                <section className="login-page__tabs" role="tablist">
                    <button
                        type="button"
                        role="tab"
                        aria-selected={typeLogin === 1}
                        className={
                            typeLogin === 1
                                ? 'login-page__tab login-page__tab--active'
                                : 'login-page__tab'
                        }
                        onClick={() => {
                            setTextUserId('');
                            setTypeLogin(1);
                        }}
                    >
                        Phone Number
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={typeLogin === 2}
                        className={
                            typeLogin === 2
                                ? 'login-page__tab login-page__tab--active'
                                : 'login-page__tab'
                        }
                        onClick={() => {
                            setTextUserId('');
                            setTypeLogin(2);
                        }}
                    >
                        Customer Id
                    </button>
                </section>

                {errorLogin.visible === 1 ? (
                    <section
                        className={`login-page__error login-page__error--severity-${errorLogin.severity}`}
                        role="alert"
                    >
                        {errorLogin.message}
                        <button
                            type="button"
                            className="login-page__error-dismiss"
                            aria-label="Dismiss error"
                            onClick={() => setErrorLogin({ visible: 0 })}
                        />
                    </section>
                ) : null}

                <form ref={formRef} className="login-page__form" onSubmit={handleLogin}>
                    <input
                        ref={userIdRef}
                        type={typeLogin === 1 ? 'tel' : 'text'}
                        name="phone"
                        placeholder={
                            typeLogin === 1 ? 'Cell Phone Number' : 'Customer Id'
                        }
                        pattern={
                            typeLogin === 1
                                ? '([0-9]{3}-[0-9]{3}-[0-9]{4})|([0-9]{10})'
                                : undefined
                        }
                        title="111-111-1111"
                        value={textUserId}
                        onChange={(e) => setTextUserId(e.target.value)}
                        required
                        autoComplete="username"
                    />
                    <input
                        type="password"
                        name="password"
                        placeholder="Password"
                        required
                        autoComplete="current-password"
                    />
                    <button className="button-empty-big" type="submit">
                        LET&apos;S GO!
                    </button>
                </form>

                <div className="login-page__footer">
                    <div>Problem with your log in?</div>
                    <a
                        href="https://oolalife.com/pages/steps-to-success-pc"
                        aria-label="reset my password"
                    >
                        Reset my Password
                    </a>
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
